import { useNode, useEditor, Element } from "@craftjs/core";
import React, { useContext, useEffect } from "react";
import { PortalSettings } from "./settings";
import GrigoraCSSRenderer from "./../css-render";
import classNames from "classnames";
import icon from "./icon";
import { createPortal } from "react-dom";
import { GrigoraDashboardContext } from "../../dashboard/context";
import SVG from "react-inlinesvg";
import SVGIcons from "../../input/icons.json";
import classnames from "classnames";
import { getDynamicCSS, getSupportProps } from "../../block-supports";
import { Container } from "../container";
import { CMSPortalSettings } from "./cms-settings";
import CMSIcon from "./cms-icon";

export const Portal = (props) => {
  const { editorDocument = document, screen } =
    useContext(GrigoraDashboardContext) ?? {};

  const {
    id,
    connectors: { connect, drag },
    selected,
    actions: { setProp },
    dom,
  } = useNode((state) => ({
    id: state.id,
    selected: state.events.selected,
    dragged: state.events.dragged,
    dom: state.dom,
  }));

  const {
    query: { node },
  } = useEditor();

  const { blockState, setBlockState } =
    useContext(GrigoraDashboardContext) ?? {};

  const {} = props;

  const isPostEditor = screen !== "site-editor";

  var blockProps = getSupportProps({
    id,
    ...props,
  });

  const wrapperClass = classnames({
    "g-portal-control": true,
    [`portal-control-${id}`]: id,
  });

  const portalClass = classnames(
    {
      "g-portal": true,
      [`portal-${id}`]: id,
      [`active`]: blockState?.(id)?.["opened"],
      "is-post-editor": isPostEditor,
    },
    blockProps.className,
  );

  useEffect(() => {
    if (isPostEditor) {
      if (blockState?.(id)?.["opened"]) {
        document.body.classList.add(`portal-open-${id}`);
      } else {
        document.body.classList.remove(`portal-open-${id}`);
      }
    }
  }, [blockState?.(id)?.["opened"]]);

  return (
    <div
      {...blockProps}
      ref={(ref) => connect(drag(ref))}
      className={wrapperClass}
      data-grigora-block="true"
      data-grigora-block-id={id}
    >
      <style>{`${getDynamicCSS(id, props)}`}</style>
      <div className="portal-box">
        <div className="top">
          <div className="title">Portal - {id}</div>
          <div
            className="button"
            onClick={(e) => {
              setBlockState(id, "opened", true);
            }}
          >
            Open Portal
          </div>
        </div>
        <div className="footer">
          This box won&apos;t be visible in the frontend. It&apos;s only present
          here so that you know which portals are available and have control
          over opening it.
        </div>
      </div>
      <style>{`${GrigoraCSSRenderer.blockCSS__Portal(id, props)}`}</style>
      {(isPostEditor
        ? editorDocument.querySelector(".grigora-editor-renderer-canvas")
        : editorDocument.querySelector("body")) &&
        // Portal for floating box
        createPortal(
          <>
            <div
              className={portalClass}
              onMouseDown={(e) => {
                setBlockState(id, "opened", false);
              }}
            >
              <div
                onMouseDown={(e) => {
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.stopPropagation();
                }}
                className="children"
              >
                <Element
                  is={Container}
                  canvas
                  id={"portal-container"}
                  width={"100%"}
                  height={"100%"}
                  backgroundLayer={{
                    mode: "color",
                    value: "#ffffff",
                  }}
                ></Element>
              </div>
            </div>
          </>,
          isPostEditor
            ? editorDocument.querySelector(".grigora-editor-renderer-canvas")
            : editorDocument.querySelector("body"),
        )}
    </div>
  );
};

// To Avoid Mangling in Production
Portal.displayName = "Portal";

Portal.craft = {
  name: "Portal",
  props: {},
  related: {
    settings: PortalSettings,
    CMSsettings: CMSPortalSettings,
    icon: icon,
    CMSIcon: CMSIcon,
    category: "portal",
    supports: [
      "grigora/mouse-movement",
      "grigora/visibility",
      "grigora/filter",
      "grigora/custom-class",
      "grigora/custom-id",
    ],
    keywords: ["Portal", "modal", "window"],
    isHiddenInPostEditor: false,
  },
  custom: {
    name: "Portal",
    slug: "grigora/portal",
  },
};
